/**
 * Cardiac Examination Data Points
 * Category: Cardiac
 */

export const cardiacDataPoints = [
  // New cardiovascular data points from user's image
  {
    id: 'pulse_volume_rhythm',
    label: 'Pulse volume and rhythm',
    category: 'Cardiac',
    subcategory: 'Cardiovascular',
    fieldType: 'multi',
    isActive: true,
    options: ['Normal', 'Other'],
    default: [],
    ui: {
      span: 6,
      variant: 'checkbox',
      display: 'checkbox'
    },
    inputConfig: {
      inputType: 'radio',
      multiSelect: false,
      allowOther: true,
      required: true,
      defaultValue: null,
      placeholder: '',
      helpText: 'Assess the pulse volume and rhythm by palpation'
    },
    code: '78564009', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'peripheral_perfusion',
    label: 'Peripheral perfusion',
    category: 'Cardiac',
    subcategory: 'Cardiovascular',
    fieldType: 'multi',
    isActive: true,
    options: ['Peripheries - Warm', 'Cold'],
    default: [],
    ui: {
      span: 6,
      variant: 'checkbox',
      display: 'checkbox'
    },
    code: '364075005', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'capillary_filling',
    label: 'Capillary filling',
    category: 'Cardiac',
    subcategory: 'Cardiovascular',
    fieldType: 'multi',
    isActive: true,
    options: ['Normal', 'Prolonged'],
    default: [],
    ui: {
      span: 6,
      variant: 'checkbox',
      display: 'checkbox'
    },
    code: '3404009', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'jvp_assessment',
    label: 'JVP',
    category: 'Cardiac',
    subcategory: 'Cardiovascular',
    fieldType: 'multi',
    isActive: true,
    options: ['Normal', 'Distended'],
    default: [],
    ui: {
      span: 6,
      variant: 'checkbox',
      display: 'checkbox'
    },
    code: '271649006', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'heart_sound_assessment',
    label: 'Heart sound',
    category: 'Cardiac',
    subcategory: 'Cardiovascular',
    fieldType: 'multi',
    isActive: true,
    options: ['Normal', 'Abnormal'],
    default: [],
    ui: {
      span: 6,
      variant: 'checkbox',
      display: 'checkbox'
    },
    code: '301113001', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'murmurs_assessment',
    label: 'Murmurs',
    category: 'Cardiac',
    subcategory: 'Cardiovascular',
    fieldType: 'multi',
    isActive: true,
    options: ['None', 'Diastolic', 'Systolic'],
    default: [],
    ui: {
      span: 6,
      variant: 'checkbox',
      display: 'checkbox'
    },
    inputConfig: {
      inputType: 'checkbox',
      multiSelect: true,
      allowOther: true,
      required: false,
      defaultValue: ['None'],
      placeholder: '',
      helpText: 'Listen for murmurs during systole and diastole. Check all that apply.'
    },
    code: '88610006', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'oedema_assessment',
    label: 'Oedema',
    category: 'Cardiac',
    subcategory: 'Cardiovascular',
    fieldType: 'multi',
    isActive: true,
    options: ['None', 'Sacral', 'Pedal'],
    default: [],
    ui: {
      span: 6,
      variant: 'checkbox',
      display: 'checkbox'
    },
    code: '267038008', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'dvt_evidence',
    label: 'Clinical evidence of DVT',
    category: 'Cardiac',
    subcategory: 'Cardiovascular',
    fieldType: 'multi',
    isActive: true,
    options: ['Yes', 'No'],
    default: [],
    ui: {
      span: 6,
      variant: 'checkbox',
      display: 'checkbox'
    },
    code: '128053003', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'heart_rate_rhythm',
    label: 'Heart Rate & Rhythm',
    category: 'Cardiac',
    subcategory: 'Auscultation',
    fieldType: 'single',
    isActive: true,
    placeholder: 'Select heart rhythm',
    options: [
      'Regular rate and rhythm',
      'Irregular rhythm',
      'Bradycardia',
      'Tachycardia',
      'Atrial fibrillation',
      'Atrial flutter',
      'Premature ventricular contractions'
    ],
    default: 'Regular rate and rhythm',
    ui: {
      span: 6,
      variant: 'select',
      display: 'dropdown'
    },
    code: '364075005', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'heart_sounds',
    label: 'Heart Sounds',
    category: 'Cardiac',
    subcategory: 'Auscultation',
    fieldType: 'multi',
    isActive: true,
    placeholder: 'Select heart sounds',
    options: [
      'S1 normal',
      'S2 normal',
      'S3 gallop',
      'S4 gallop',
      'Murmur present',
      'No murmur',
      'Rub present',
      'Click present'
    ],
    default: ['S1 normal', 'S2 normal', 'No murmur'],
    ui: {
      span: 6,
      variant: 'checkbox',
      display: 'chip'
    },
    code: '301113001', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'murmur_details',
    label: 'Murmur Details',
    category: 'Cardiac',
    subcategory: 'Auscultation',
    fieldType: 'panel',
    isActive: true,
    nestedSubFields: true,
    subFields: [
      {
        id: 'murmur_grade',
        label: 'Grade',
        fieldType: 'single',
        options: ['1/6', '2/6', '3/6', '4/6', '5/6', '6/6'],
        ui: { span: 3 }
      },
      {
        id: 'murmur_timing',
        label: 'Timing',
        fieldType: 'single',
        options: ['Systolic', 'Diastolic', 'Continuous'],
        ui: { span: 3 }
      },
      {
        id: 'murmur_location',
        label: 'Location',
        fieldType: 'single',
        options: ['Apex', 'LLSB', 'LUSB', 'RUSB', 'RLSB'],
        ui: { span: 3 }
      },
      {
        id: 'murmur_radiation',
        label: 'Radiation',
        fieldType: 'text',
        placeholder: 'e.g., to axilla, neck',
        ui: { span: 3 }
      }
    ],
    code: '88610006', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'jugular_venous_pressure',
    label: 'Jugular Venous Pressure',
    category: 'Cardiac',
    subcategory: 'Inspection',
    fieldType: 'single',
    isActive: true,
    placeholder: 'Select JVP finding',
    options: [
      'Normal (< 4cm)',
      'Mildly elevated (4-6cm)',
      'Moderately elevated (6-8cm)',
      'Severely elevated (> 8cm)',
      'Not visible',
      'Unable to assess'
    ],
    default: 'Normal (< 4cm)',
    ui: {
      span: 4,
      variant: 'select',
      display: 'dropdown'
    },
    units: 'cm H2O',
    code: '271649006', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'peripheral_pulses',
    label: 'Peripheral Pulses',
    category: 'Cardiac',
    subcategory: 'Palpation',
    fieldType: 'panel',
    isActive: true,
    nestedSubFields: true,
    subFields: [
      {
        id: 'radial_pulse',
        label: 'Radial',
        fieldType: 'single',
        options: ['0 (Absent)', '1+ (Weak)', '2+ (Normal)', '3+ (Bounding)'],
        default: '2+ (Normal)',
        ui: { span: 3 }
      },
      {
        id: 'brachial_pulse',
        label: 'Brachial',
        fieldType: 'single',
        options: ['0 (Absent)', '1+ (Weak)', '2+ (Normal)', '3+ (Bounding)'],
        default: '2+ (Normal)',
        ui: { span: 3 }
      },
      {
        id: 'carotid_pulse',
        label: 'Carotid',
        fieldType: 'single',
        options: ['0 (Absent)', '1+ (Weak)', '2+ (Normal)', '3+ (Bounding)'],
        default: '2+ (Normal)',
        ui: { span: 3 }
      },
      {
        id: 'femoral_pulse',
        label: 'Femoral',
        fieldType: 'single',
        options: ['0 (Absent)', '1+ (Weak)', '2+ (Normal)', '3+ (Bounding)'],
        default: '2+ (Normal)',
        ui: { span: 3 }
      },
      {
        id: 'dorsalis_pedis',
        label: 'Dorsalis Pedis',
        fieldType: 'single',
        options: ['0 (Absent)', '1+ (Weak)', '2+ (Normal)', '3+ (Bounding)'],
        default: '2+ (Normal)',
        ui: { span: 6 }
      },
      {
        id: 'posterior_tibial',
        label: 'Posterior Tibial',
        fieldType: 'single',
        options: ['0 (Absent)', '1+ (Weak)', '2+ (Normal)', '3+ (Bounding)'],
        default: '2+ (Normal)',
        ui: { span: 6 }
      }
    ],
    code: '113011001', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'cardiac_edema',
    label: 'Edema',
    category: 'Cardiac',
    subcategory: 'Inspection',
    fieldType: 'single',
    isActive: true,
    placeholder: 'Select edema severity',
    options: ['No edema', 'Trace edema', '1+ (Mild)', '2+ (Moderate)', '3+ (Severe)', '4+ (Very severe)'],
    default: 'No edema',
    ui: {
      span: 4,
      variant: 'select',
      display: 'dropdown'
    },
    code: '267038008', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'chest_pain_assessment',
    label: 'Chest Pain Assessment',
    category: 'Cardiac',
    subcategory: 'Symptoms',
    fieldType: 'panel',
    isActive: true,
    nestedSubFields: true,
    subFields: [
      {
        id: 'chest_pain_present',
        label: 'Chest Pain Present',
        fieldType: 'checkbox',
        default: false,
        ui: { span: 12 }
      },
      {
        id: 'pain_character',
        label: 'Character',
        fieldType: 'single',
        options: ['Sharp', 'Dull', 'Crushing', 'Burning', 'Stabbing', 'Pressure'],
        ui: { span: 4 }
      },
      {
        id: 'pain_location',
        label: 'Location',
        fieldType: 'single',
        options: ['Substernal', 'Left chest', 'Right chest', 'Epigastric', 'Back'],
        ui: { span: 4 }
      },
      {
        id: 'pain_radiation',
        label: 'Radiation',
        fieldType: 'multi',
        options: ['Left arm', 'Right arm', 'Jaw', 'Back', 'Neck', 'Epigastrium'],
        ui: { span: 4 }
      }
    ],
    code: '29857009', // SNOMED CT
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
]

export default cardiacDataPoints
